import React from 'react';

/**
 * Modern, customizable Loader component for TaskScheduler
 * 
 * Variants:
 * - 'inline' (default): Small spinner for inside buttons or inline status
 * - 'page': Centered card loading view for page sections or table containers
 * - 'fullscreen': Full screen fixed overlay with backdrop blur
 * - 'overlay': Absolute backdrop overlay for cards/modals
 * 
 * Props:
 * @param {string} variant - 'inline' | 'page' | 'fullscreen' | 'overlay'
 * @param {string} size - 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * @param {string} text - Loading label / status text
 * @param {boolean} isDarkMode - Dark mode theme toggle
 * @param {string} color - Primary accent color hex/rgb
 * @param {object} style - Additional container style overrides
 * @param {string} className - Additional CSS class name
 */

export const Loader = ({
  variant = 'inline',
  size = 'md',
  text = '',
  isDarkMode = false,
  color = '#667eea',
  style = {},
  className = ''
}) => {
  // Size mapping in pixels
  const sizeMap = {
    xs: { ring: 16, stroke: 2, font: '0.8rem', icon: '0.75rem' },
    sm: { ring: 20, stroke: 2.5, font: '0.875rem', icon: '0.875rem' },
    md: { ring: 32, stroke: 3, font: '1rem', icon: '1.1rem' },
    lg: { ring: 56, stroke: 4, font: '1.15rem', icon: '1.5rem' },
    xl: { ring: 90, stroke: 5, font: '1.35rem', icon: '2.2rem' }
  };

  const currentSize = sizeMap[size] || sizeMap.md;

  // Theme colors
  const textColor = isDarkMode ? '#f3f4f6' : '#1f2937';
  const subtextColor = isDarkMode ? '#9ca3af' : '#6b7280';
  const cardBg = isDarkMode ? 'rgba(31, 41, 55, 0.85)' : 'rgba(255, 255, 255, 0.9)';
  const borderColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)';

  // 1. INLINE VARIANT (Buttons & Compact text)
  if (variant === 'inline') {
    return (
      <span
        className={`ts-loader-inline ${className}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          verticalAlign: 'middle',
          ...style
        }}
      >
        <svg
          width={currentSize.ring}
          height={currentSize.ring}
          viewBox="0 0 50 50"
          style={{
            animation: 'ts-spin 0.85s linear infinite',
            flexShrink: 0
          }}
        >
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke={color}
            strokeWidth={currentSize.stroke * 1.5}
            strokeOpacity="0.25"
          />
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke={color}
            strokeWidth={currentSize.stroke * 1.5}
            strokeDasharray="90, 150"
            strokeDashoffset="0"
            strokeLinecap="round"
          />
        </svg>
        {text && (
          <span style={{ fontSize: currentSize.font, fontWeight: 500 }}>
            {text}
          </span>
        )}
        <style>{`
          @keyframes ts-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </span>
    );
  }

  // 2. PAGE / CONTAINER VARIANT
  if (variant === 'page') {
    const pageRingSize = size === 'md' ? 64 : currentSize.ring;
    return (
      <div
        className={`ts-loader-page ${className}`}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '3rem 2rem',
          minHeight: '260px',
          width: '100%',
          borderRadius: '1.25rem',
          background: cardBg,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: `1px solid ${borderColor}`,
          boxShadow: isDarkMode 
            ? '0 10px 30px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)' 
            : '0 10px 30px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
          margin: '1rem 0',
          position: 'relative',
          overflow: 'hidden',
          ...style
        }}
      >
        {/* Glow ambient circle */}
        <div style={{
          position: 'absolute',
          width: '180px',
          height: '180px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${color}25 0%, transparent 70%)`,
          animation: 'ts-pulse-glow 3s ease-in-out infinite alternate',
          pointerEvents: 'none'
        }} />

        {/* Outer Ring & Center Icon */}
        <div style={{ position: 'relative', width: pageRingSize, height: pageRingSize, marginBottom: '1.25rem' }}>
          <svg
            width={pageRingSize}
            height={pageRingSize}
            viewBox="0 0 100 100"
            style={{
              animation: 'ts-spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite',
              transformOrigin: 'center center'
            }}
          >
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke={isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'}
              strokeWidth="6"
            />
            <circle
              cx="50"
              cy="50"
              r="42"
              fill="none"
              stroke={`url(#ts-loader-grad-${size})`}
              strokeWidth="6"
              strokeDasharray="180 80"
              strokeLinecap="round"
            />
            <defs>
              <linearGradient id={`ts-loader-grad-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#667eea" />
                <stop offset="50%" stopColor="#764ba2" />
                <stop offset="100%" stopColor="#f59e0b" />
              </linearGradient>
            </defs>
          </svg>

          {/* Centered Pulsing Icon */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: color,
              fontSize: '1.25rem',
              animation: 'ts-float-icon 2s ease-in-out infinite'
            }}
          >
            <i className="fas fa-tasks" />
          </div>
        </div>

        {/* Text and bouncing dots */}
        {text && (
          <div style={{ textAlign: 'center', zIndex: 1 }}>
            <p style={{
              margin: '0 0 0.5rem 0',
              fontSize: currentSize.font,
              fontWeight: 600,
              color: textColor,
              letterSpacing: '-0.01em'
            }}>
              {text}
            </p>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginTop: '0.25rem' }}>
          {[0, 0.2, 0.4].map((delay, i) => (
            <span
              key={i}
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: color,
                animation: `ts-bounce-dot 1.2s ease-in-out ${delay}s infinite`
              }}
            />
          ))}
        </div>

        <style>{`
          @keyframes ts-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes ts-pulse-glow {
            0% { transform: scale(0.85); opacity: 0.4; }
            100% { transform: scale(1.15); opacity: 0.8; }
          }
          @keyframes ts-float-icon {
            0%, 100% { transform: translate(-50%, -50%) scale(0.95); }
            50% { transform: translate(-50%, -50%) scale(1.1); }
          }
          @keyframes ts-bounce-dot {
            0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
            40% { transform: translateY(-6px); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  // 3. OVERLAY VARIANT (Card / Modal Backdrop loading overlay)
  if (variant === 'overlay') {
    return (
      <div
        className={`ts-loader-overlay ${className}`}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDarkMode ? 'rgba(17, 24, 39, 0.75)' : 'rgba(255, 255, 255, 0.75)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
          borderRadius: 'inherit',
          gap: '0.75rem',
          ...style
        }}
      >
        <Loader variant="inline" size={size} color={color} isDarkMode={isDarkMode} />
        {text && (
          <span style={{ fontSize: '0.925rem', fontWeight: 600, color: textColor }}>
            {text}
          </span>
        )}
      </div>
    );
  }

  // 4. FULLSCREEN VARIANT
  if (variant === 'fullscreen') {
    const bgGradient = isDarkMode
      ? 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #1e293b 100%)'
      : 'linear-gradient(135deg, #f8fafc 0%, #e0e7ff 50%, #f1f5f9 100%)';

    return (
      <div
        className={`ts-loader-fullscreen ${className}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: bgGradient,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          gap: '2rem',
          ...style
        }}
      >
        {/* Glowing background orb */}
        <div style={{
          position: 'absolute',
          width: '320px',
          height: '320px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.25) 0%, transparent 70%)',
          animation: 'ts-pulse-glow 4s ease-in-out infinite alternate',
          pointerEvents: 'none'
        }} />

        {/* Center Spinner Ring */}
        <div style={{ position: 'relative', width: '100px', height: '100px' }}>
          <svg
            width="100"
            height="100"
            viewBox="0 0 100 100"
            style={{
              animation: 'ts-spin 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite',
              filter: 'drop-shadow(0 0 15px rgba(99, 102, 241, 0.5))'
            }}
          >
            <circle
              cx="50"
              cy="50"
              r="44"
              fill="none"
              stroke={isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}
              strokeWidth="5"
            />
            <circle
              cx="50"
              cy="50"
              r="44"
              fill="none"
              stroke="url(#ts-fs-gradient)"
              strokeWidth="5"
              strokeDasharray="160 120"
              strokeLinecap="round"
            />
            <defs>
              <linearGradient id="ts-fs-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4F46E5" />
                <stop offset="50%" stopColor="#818CF8" />
                <stop offset="100%" stopColor="#c084fc" />
              </linearGradient>
            </defs>
          </svg>

          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: isDarkMode ? '#818CF8' : '#4F46E5',
            fontSize: '2rem'
          }}>
            <i className="fas fa-calendar-check" />
          </div>
        </div>

        {/* App Title & Status */}
        <div style={{ textAlign: 'center', zIndex: 1 }}>
          <h2 style={{
            margin: '0 0 0.5rem 0',
            fontSize: '1.75rem',
            fontWeight: 800,
            color: textColor,
            letterSpacing: '-0.02em'
          }}>
            Task Scheduler
          </h2>
          <p style={{
            margin: 0,
            fontSize: '1.05rem',
            fontWeight: 500,
            color: subtextColor
          }}>
            {text || 'Loading...'}
          </p>
        </div>

        {/* Dots */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {[0, 0.2, 0.4].map((delay, i) => (
            <span
              key={i}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: isDarkMode ? '#818CF8' : '#4F46E5',
                boxShadow: '0 0 8px rgba(99, 102, 241, 0.6)',
                animation: `ts-bounce-dot 1.2s ease-in-out ${delay}s infinite`
              }}
            />
          ))}
        </div>

        <style>{`
          @keyframes ts-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes ts-pulse-glow {
            0% { transform: scale(0.85); opacity: 0.4; }
            100% { transform: scale(1.15); opacity: 0.8; }
          }
          @keyframes ts-bounce-dot {
            0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
            40% { transform: translateY(-8px); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  return null;
};

export default Loader;
